// Sample Tracking → Sample Cancellation — wire types.
// Backend: Application/SampleCancellation/SampleCancellationModels.cs.

/** One sample TEST in the queue (all parameter rows of that test, grouped). */
export interface CancellationQueueRow {
  sampleOid:      string;
  testMethodOid:  string;
  isCancelled:    boolean;
  sampleId:       string | null;
  sampleName:     string | null;
  jobId:          string | null;
  client:         string | null;
  projectId:      string | null;
  projectName:    string | null;
  siteName:       string | null;
  test:           string | null;
  method:         string | null;
  /** Prep batch id(s), comma-joined; null = not in a prep batch. */
  prepBatch:      string | null;
  /** For a cancelled row: the stage it was cancelled from. */
  stage:          string | null;
  dateReceived:   string | null;
  status:         string | null;
  /** What cancelling it now will do (queue / batch impact) — server wording. */
  impact:         string | null;
  parameterCount: number;
}

export interface CancellationReasonCode {
  oid:                string;
  code:               string | null;
  reason:             string | null;
  pendingSamplePrep:  boolean;
  pendingResultEntry: boolean;
  commentRequired:    boolean;
  defaultComment:     string | null;
  /** Legacy enum ReasonCancellationBillable — see BILLABLE_LABELS. */
  billable:           number;
  sortOrder:          number;
  active:             boolean;
}

export interface CancellationKey { sampleOid: string; testMethodOid: string }

/** One test a cancel / restore actually changed. */
export interface CancellationChange {
  sampleId:             string | null;
  test:                 string | null;
  stage:                string | null;
  /** Prep batch(es) the test was removed from, comma-joined; null = none.
   *  A QC batch is never touched — a test in one cannot be cancelled. */
  prepBatchRemoved: string | null;
  /** Prep-batch QC rows (Dup / MS / MSD ...) removed with the sample, comma-joined codes. */
  qcRemoved:        string | null;
}

export interface CancellationResult {
  /** Tests this call actually changed — fewer than `requested` when some were
   *  skipped (a result was entered or it joined a QC batch meanwhile, already
   *  changed by someone else, or the reason code's stage rule). */
  changed:   number;
  requested: number;
  rows:      CancellationChange[];
}

export const STAGE_PREP  = 'Pending Sample Prep';
export const STAGE_ENTRY = 'Pending Result Entry';

/** Legacy enum order: No · Yes · IfAfterPrep · LabDecision. */
export const BILLABLE_LABELS = ['No', 'Yes', 'If after prep', 'Lab decision'];

/** Grid row id — a queue row is identified by sample + test + cancelled state. */
export const rowKey = (r: CancellationQueueRow) =>
  `${r.sampleOid}|${r.testMethodOid}|${r.isCancelled ? 1 : 0}`;
